import { type BoundingBoxDto, CountryCode, Language, MarketCountryCode } from '@generated/search-bff';

export function ensureHttpsPrefix(url: string): string {
  if (!/^https?:\/\//i.test(url)) {
    return 'https://' + url;
  }

  return url.replace(/^http:\/\//i, 'https://');
}

const YOUTUBE_URL_REGEX = /(?:(?:youtube(?:-nocookie)?\.com)|(?:youtu\.be))\/.*([\w-]{11}).*/;

export function isYoutubeUrl(url: string): boolean {
  return YOUTUBE_URL_REGEX.test(url);
}

export function getVideoEmbedUrl(url: string): string {
  const vimeoRegex = /https?:\/\/(?:www\.)?vimeo\.com\/(?:\D*\/)?(?<videoId>\d+)(?:\/(?<hash>[\w-]+))?\/?/;
  const youtubeVideoId = url.match(YOUTUBE_URL_REGEX)?.[1];
  const vimeoMatch = url.match(vimeoRegex);

  if (youtubeVideoId) {
    return `https://www.youtube-nocookie.com/embed/${youtubeVideoId}`;
  }

  if (vimeoMatch?.groups?.videoId) {
    const { videoId, hash } = vimeoMatch.groups;
    const base = `https://player.vimeo.com/video/${videoId}`;
    return hash ? `${base}?h=${encodeURIComponent(hash)}` : base;
  }

  url = ensureHttpsPrefix(url);
  return url;
}

export function prependLocaleToPath(pathOrUrl: string, language: Language, countryCode?: MarketCountryCode): string {
  const prefix = [countryCode?.toLowerCase(), language].filter(Boolean).join('/');

  // Handle absolute URLs
  const urlMatch = pathOrUrl.match(/^(https?:\/\/[^/]+)(\/.*)?$/);
  if (urlMatch) {
    const [, origin, pathname = ''] = urlMatch;
    if (pathname.startsWith('/') || pathname.startsWith('?') || pathname === '') {
      return `${origin}/${prefix}${pathname || '/'}`;
    }
    return `${origin}/${prefix}/${pathname}`;
  }

  // Handle relative paths
  if (pathOrUrl.startsWith('/') || pathOrUrl.startsWith('?')) {
    return `/${prefix}${pathOrUrl}`;
  }
  return `/${prefix}/${pathOrUrl}`;
}

export function getExposePath(
  listing: { isDevelopmentProject?: boolean; id: string },
  language: Language,
  countryCode: MarketCountryCode,
): string {
  return prependLocaleToPath(`/${listing.isDevelopmentProject ? 'projects' : 'exposes'}/${listing.id}`, language, countryCode);
}

export const replaceLocaleInPath = (pathOrUrl: string, language: Language, countryCode?: MarketCountryCode): string => {
  const localeRegex = /^\/(?:(?<countryCode>[A-Za-z]{2}|private-office)(?:\/|$))?(?<language>[A-Za-z]{2})(?:\/|$)/;

  // Handle absolute URLs
  const urlMatch = pathOrUrl.match(/^(https?:\/\/[^/]+)(\/.*)?$/);
  if (urlMatch) {
    const [, origin, pathname = '/'] = urlMatch;
    const existingLocaleMatch = pathname.match(localeRegex);

    // Check if the pathname has a locale prefix
    if (existingLocaleMatch) {
      const oldCountryCode = existingLocaleMatch.groups?.countryCode as MarketCountryCode | undefined;
      const newPathname = pathname.replace(localeRegex, prependLocaleToPath('', language, countryCode ?? oldCountryCode));
      return `${origin}${newPathname}`;
    } else {
      return prependLocaleToPath(pathOrUrl, language, countryCode);
    }
  }

  // Handle relative paths
  const existingLocaleMatch = pathOrUrl.match(localeRegex);
  if (existingLocaleMatch) {
    const oldCountryCode = existingLocaleMatch.groups?.countryCode as MarketCountryCode | undefined;
    return pathOrUrl.replace(localeRegex, prependLocaleToPath('', language, countryCode ?? oldCountryCode));
  } else {
    return prependLocaleToPath(pathOrUrl, language, countryCode);
  }
};

/**
 * Map that associates a countryCode with an object containing an array of available languages and a default language.
 * This needs to stay in sync with the redirect rules and url maps in the Cloud team's code. Right now these are the relevant files:
 * - Search paths mapping: https://git.engelvoelkers.com/ev/cloud-platform/lb-backend-services/-/blob/main/dev/url-maps/terraform.tfvars#L401
 * - Redirect rules: https://git.engelvoelkers.com/ev/cloud-platform/lb-backend-services/-/blob/main/prod/url-maps/main.tf
 * - Country language mapping: https://docs.google.com/spreadsheets/d/1jyK-XZ2TaV_-iDowLVCT9KruI8HuE3ZfKrSPn_npc_Y/edit?gid=977604131#gid=977604131
 *
 * @typeParam CountryCode - The type of the country code.
 * @typeParam Language - The type of the language.
 */
export const COUNTRY_LANGUAGE_RECORD: Record<MarketCountryCode, { languages: Language[]; defaultLanguage: Language }> = {
  [MarketCountryCode.AD]: { languages: ['es', 'en', 'ca', 'fr'], defaultLanguage: 'en' },
  [MarketCountryCode.AE]: { languages: ['en', 'de'], defaultLanguage: 'en' },
  [MarketCountryCode.AT]: { languages: ['de', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.BE]: { languages: ['nl', 'en', 'fr'], defaultLanguage: 'en' },
  [MarketCountryCode.BS]: { languages: ['en'], defaultLanguage: 'en' },
  [MarketCountryCode.BZ]: { languages: ['es', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.CA]: { languages: ['en', 'fr', 'de', 'es'], defaultLanguage: 'en' },
  [MarketCountryCode.CH]: { languages: ['de', 'en', 'fr', 'it'], defaultLanguage: 'en' },
  [MarketCountryCode.CL]: { languages: ['es', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.CO]: { languages: ['es', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.CR]: { languages: ['es', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.CZ]: { languages: ['cs', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.DE]: { languages: ['de', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.DK]: { languages: ['da', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.ES]: { languages: ['es', 'ca', 'de', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.FR]: { languages: ['fr', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.GR]: { languages: ['el', 'de', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.HR]: { languages: ['hr', 'en', 'de'], defaultLanguage: 'en' },
  [MarketCountryCode.HU]: { languages: ['hu', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.IE]: { languages: ['en', 'de'], defaultLanguage: 'en' },
  [MarketCountryCode.IT]: { languages: ['it', 'en', 'de'], defaultLanguage: 'en' },
  [MarketCountryCode.KY]: { languages: ['en'], defaultLanguage: 'en' },
  [MarketCountryCode.LI]: { languages: ['de', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.LU]: { languages: ['fr', 'de', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.MF]: { languages: ['fr', 'de', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.MG]: { languages: ['fr', 'de', 'en'], defaultLanguage: 'en' }, // this only exists on dev/stage
  [MarketCountryCode.MU]: { languages: ['fr', 'de', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.MX]: { languages: ['es', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.NL]: { languages: ['nl', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.PA]: { languages: ['es', 'en', 'fr'], defaultLanguage: 'en' },
  [MarketCountryCode.PT]: { languages: ['pt', 'en', 'de'], defaultLanguage: 'en' },
  [MarketCountryCode.TC]: { languages: ['en'], defaultLanguage: 'en' },
  [MarketCountryCode.US]: { languages: ['en', 'de', 'es', 'fr', 'it'], defaultLanguage: 'en' },
  [MarketCountryCode.UY]: { languages: ['es', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode.VG]: { languages: ['en'], defaultLanguage: 'en' },
  [MarketCountryCode.ZA]: { languages: ['en'], defaultLanguage: 'en' },
  [MarketCountryCode.PL]: { languages: ['pl', 'en'], defaultLanguage: 'en' },
  [MarketCountryCode['private-office']]: { languages: ['en', 'de', 'fr', 'it', 'es'], defaultLanguage: 'en' },
};

export const COUNTRY_LANGUAGE_MAP = new Map(
  Object.entries(COUNTRY_LANGUAGE_RECORD) as Array<[MarketCountryCode, { languages: Language[]; defaultLanguage: Language }]>,
);

/**
 * Validates the combination of language and countryCode in a search path. It applies a fallback language if the provided combination of countryCode and language is invalid.
 *
 * @param path - The search path to validate.
 * @returns A validated search path, with prepended fallback language and countryCode if necessary. Will fallback to "/de/en" if countryCode is missing or invalid.
 */
export const validateLanguageForCountry = (path: string, countryCode: MarketCountryCode, language: Language): string => {
  if (!path) {
    return replaceLocaleInPath('/propertysearch', language, countryCode);
  }

  if (COUNTRY_LANGUAGE_MAP.has(countryCode) && COUNTRY_LANGUAGE_MAP.get(countryCode)?.languages.includes(language)) {
    return path;
  } else if (COUNTRY_LANGUAGE_MAP.has(countryCode)) {
    return replaceLocaleInPath(path, COUNTRY_LANGUAGE_MAP.get(countryCode)!.defaultLanguage, countryCode);
  } else {
    console.error('Invalid countryCode');
    return replaceLocaleInPath(path, Language.en, MarketCountryCode.DE);
  }
};

type CountryCodeLanguage = {
  countryCode: MarketCountryCode;
  language: Language;
};

/**
 * Map that associates a language without an accompanying countryCode with an object containing the corresponding countryCode and language combination.
 * This is used to redirect pages that a user has bookmarked from a while ago, when these language-only URLs were possible, to the appropriate URL that contains a countryCode.
 *
 * @typeParam CountryCode - The type of the country code.
 * @typeParam Language - The type of the language.
 */

export const LANGUAGE_ONLY_COUNTRYCODE_LANGUAGE_MAP: Record<Language, CountryCodeLanguage> = {
  [Language.en]: { countryCode: MarketCountryCode.DE, language: Language.en },
  [Language.de]: { countryCode: MarketCountryCode.DE, language: Language.de },
  [Language.fr]: { countryCode: MarketCountryCode.FR, language: Language.fr },
  [Language.it]: { countryCode: MarketCountryCode.IT, language: Language.it },
  [Language.es]: { countryCode: MarketCountryCode.ES, language: Language.es },
  [Language.el]: { countryCode: MarketCountryCode.GR, language: Language.el },
  [Language.da]: { countryCode: MarketCountryCode.DK, language: Language.da },
  [Language.pt]: { countryCode: MarketCountryCode.PT, language: Language.pt },
  [Language.ca]: { countryCode: MarketCountryCode.ES, language: Language.ca },
  [Language.cs]: { countryCode: MarketCountryCode.CZ, language: Language.cs },
  [Language.nl]: { countryCode: MarketCountryCode.NL, language: Language.nl },
  [Language.hr]: { countryCode: MarketCountryCode.HR, language: Language.hr },
  [Language.hu]: { countryCode: MarketCountryCode.HU, language: Language.hu },
  [Language.pl]: { countryCode: MarketCountryCode.PL, language: Language.pl },
};

export const getCountryCodeLanguageRedirectObject = (language: Language, countryCode?: MarketCountryCode): CountryCodeLanguage => {
  return countryCode ? { countryCode, language } : LANGUAGE_ONLY_COUNTRYCODE_LANGUAGE_MAP[language];
};

/**
 * Map that associates a countryCode with the placeId for that country.
 * This can be used to assign a SERP page with a specific countryCode to automatically show results from that country.
 *
 * @typeParam CountryCode - The type of the country code.
 * @typeParam string - Representing the placeId of that country.
 */

export const COUNTRYCODE_PLACEID_MAP: Record<Exclude<MarketCountryCode, 'private-office'>, string> = {
  [MarketCountryCode.AD]: 'ChIJlfCemC71pRIRkn_qeNc-yQc',
  [MarketCountryCode.AE]: 'ChIJvRKrsd9IXj4RpwoIwFYv0zM',
  [MarketCountryCode.AT]: 'ChIJfyqdJZsHbUcRr8Hk3XvUEhA',
  [MarketCountryCode.BE]: 'ChIJl5fz7WR9wUcR8g_mObTy60c',
  [MarketCountryCode.BS]: 'ChIJPQ9Isjua1ogR5XnHaoO0PhM',
  [MarketCountryCode.BZ]: 'ChIJ-foSwT5bXI8RTuMeP02KSvY',
  [MarketCountryCode.CA]: 'ChIJ2WrMN9MDDUsRpY9Doiq3aJk',
  [MarketCountryCode.CH]: 'ChIJYW1Zb-9kjEcRFXvLDxG1Vlw',
  [MarketCountryCode.CL]: 'ChIJL68lBEHFYpYRHbkCERPhBQU',
  [MarketCountryCode.CO]: 'ChIJo5QVrjqkFY4RQKPy7wSaDZo',
  [MarketCountryCode.CR]: 'ChIJJcmsIWLlko8RK5qBNSX3VGI',
  [MarketCountryCode.CZ]: 'ChIJQ4Ld14-UC0cRb1jb03UcZvg',
  [MarketCountryCode.DE]: 'ChIJa76xwh5ymkcRW-WRjmtd6HU',
  [MarketCountryCode.DK]: 'ChIJ-1-U7rYnS0YRzZLgw9BDh1I',
  [MarketCountryCode.ES]: 'ChIJi7xhMnjjQgwR7KNoB5Qs7KY',
  [MarketCountryCode.FR]: 'ChIJMVd4MymgVA0R99lHx5Y__Ws',
  [MarketCountryCode.GR]: 'ChIJY2xxEcdKWxMRHS2a3HUXOjY',
  [MarketCountryCode.HR]: 'ChIJ7ZXdCghBNBMRfxtm4STA86A',
  [MarketCountryCode.HU]: 'ChIJw-Q333uDQUcREBAeDCnEAAA',
  [MarketCountryCode.IE]: 'ChIJ-ydAXOS6WUgRCPTbzjQSfM8',
  [MarketCountryCode.IT]: 'ChIJA9KNRIL-1BIRb15jJFz1LOI',
  [MarketCountryCode.KY]: 'ChIJKaq4Lz6GJY8RXnFwh9PERXA',
  [MarketCountryCode.LI]: 'ChIJ_S9HHUQxm0cRibFa3Ta16mA',
  [MarketCountryCode.LU]: 'ChIJRyEhyrlFlUcR75LTAvZg22Q',
  [MarketCountryCode.MU]: 'ChIJyXRE-U1QfCERMdBrEcLZA0I',
  [MarketCountryCode.MG]: 'ChIJRYUj6uOk0SERiBN7l8HjRFI',
  [MarketCountryCode.MX]: 'ChIJU1NoiDs6BIQREZgJa760ZO0',
  [MarketCountryCode.NL]: 'ChIJu-SH28MJxkcRnwq9_851obM',
  [MarketCountryCode.PA]: 'ChIJ4yu-yIMVpo8Rz5ulH03g7nk',
  [MarketCountryCode.PT]: 'ChIJ1SZCvy0kMgsRQfBOHAlLuCo',
  [MarketCountryCode.TC]: 'ChIJdWu7vlVfS4kR3sGNAoDHEX4',
  [MarketCountryCode.US]: 'ChIJCzYy5IS16lQRQrfeQ5K5Oxw',
  [MarketCountryCode.UY]: 'ChIJCd5f-zoHdZURSlIWYGGWVUo',
  [MarketCountryCode.VG]: 'ChIJuaza8mUQBYwRBRzFGx2JHfk',
  [MarketCountryCode.ZA]: 'ChIJURLu2YmmNBwRoOikHwxjXeg',
  [MarketCountryCode.MF]: 'ChIJ_U5xCKxvDowRp4huTgqDGbk',
  [MarketCountryCode.PL]: 'ChIJuwtkpGSZAEcR6lXMScpzdQk',
};

export const PLACEID_COUNTRYCODE_MAP = new Map(
  Object.entries(COUNTRYCODE_PLACEID_MAP).map(([countryCode, placeId]) => [
    placeId,
    countryCode as Exclude<MarketCountryCode, 'private-office'>,
  ]),
);

export const COUNTRYCODE_BOUNDINGBOX_MAP: Record<CountryCode, BoundingBoxDto> = {
  [CountryCode.AD]: {
    northEast: {
      lat: 42.655_893_8,
      lng: 1.786_681,
    },
    southWest: {
      lat: 42.428_711_799_999_99,
      lng: 1.413_543_6,
    },
  },
  [CountryCode.AE]: {
    northEast: {
      lat: 26.0765,
      lng: 56.439_500_1,
    },
    southWest: {
      lat: 22.631_513_8,
      lng: 51.457_650_4,
    },
  },
  [CountryCode.AT]: {
    northEast: {
      lat: 49.020_530_5,
      lng: 17.160_801_8,
    },
    southWest: {
      lat: 46.372_335_799_999_99,
      lng: 9.530_734_8,
    },
  },
  [CountryCode.AW]: {
    northEast: {
      lat: 12.6306,
      lng: -69.865,
    },
    southWest: {
      lat: 12.411_399_9,
      lng: -70.0641,
    },
  },
  [CountryCode.BE]: {
    northEast: {
      lat: 51.505_124_2,
      lng: 6.4081,
    },
    southWest: {
      lat: 49.496_951_8,
      lng: 2.524_099_9,
    },
  },
  [CountryCode.BS]: {
    northEast: {
      lat: 27.263_412,
      lng: -72.709_753_900_000_01,
    },
    southWest: {
      lat: 20.908_273_5,
      lng: -80.477_560_3,
    },
  },
  [CountryCode.BZ]: {
    northEast: {
      lat: 18.495_941_9,
      lng: -87.412_699_899_999_99,
    },
    southWest: {
      lat: 15.885_618_9,
      lng: -89.227_587_899_999_99,
    },
  },
  [CountryCode.CA]: {
    northEast: {
      lat: 62.936_823_783_415_264,
      lng: -51.516_029_780_958_72,
    },
    southWest: {
      lat: 41.675_840_105_193_26,
      lng: -140.355_944_176_447_8,
    },
  },
  [CountryCode.CH]: {
    northEast: {
      lat: 47.808_454_6,
      lng: 10.492_340_1,
    },
    southWest: {
      lat: 45.817_92,
      lng: 5.955_897_8,
    },
  },
  [CountryCode.CL]: {
    northEast: {
      lat: -17.498_364_9,
      lng: -66.3327,
    },
    southWest: {
      lat: -56.6233,
      lng: -110.0281,
    },
  },
  [CountryCode.CO]: {
    northEast: {
      lat: 16.070_937_6,
      lng: -66.846_312_2,
    },
    southWest: {
      lat: -4.227_109_899_999_999,
      lng: -81.8317,
    },
  },
  [CountryCode.CR]: {
    northEast: {
      lat: 11.219_681,
      lng: -82.518_300_099_999_99,
    },
    southWest: {
      lat: 5.298_118_3,
      lng: -87.361_640_799_999_99,
    },
  },
  [CountryCode.CZ]: {
    northEast: {
      lat: 51.055_718_5,
      lng: 18.859_236_1,
    },
    southWest: {
      lat: 48.551_809_2,
      lng: 12.090_589,
    },
  },
  [CountryCode.DE]: {
    northEast: {
      lat: 55.0815,
      lng: 15.041_896_2,
    },
    southWest: {
      lat: 47.270_114,
      lng: 5.866_342_5,
    },
  },
  [CountryCode.DK]: {
    northEast: {
      lat: 58.028_46,
      lng: 15.2298,
    },
    southWest: {
      lat: 54.431_700_1,
      lng: 7.855_200_099_999_999,
    },
  },
  [CountryCode.ES]: {
    northEast: {
      lat: 43.8504,
      lng: 4.6362,
    },
    southWest: {
      lat: 27.4985,
      lng: -18.264_800_1,
    },
  },
  [CountryCode.FR]: {
    northEast: {
      lat: 51.325,
      lng: 8.233_209_267_754_392,
    },
    southWest: {
      lat: 42.334_586_725_798_94,
      lng: -5.47,
    },
  },
  [CountryCode.GR]: {
    northEast: {
      lat: 41.750_250_5,
      lng: 29.654,
    },
    southWest: {
      lat: 34.5428,
      lng: 18.994_867_8,
    },
  },
  [CountryCode.HR]: {
    northEast: {
      lat: 46.555_020_4,
      lng: 19.447_376_7,
    },
    southWest: {
      lat: 42.338_508_7,
      lng: 13.3649,
    },
  },
  [CountryCode.HU]: {
    northEast: {
      lat: 48.585_234,
      lng: 22.896_685_2,
    },
    southWest: {
      lat: 45.737_076_8,
      lng: 16.113_682_5,
    },
  },
  [CountryCode.IE]: {
    northEast: {
      lat: 55.4602,
      lng: -5.668_900_100_000_001,
    },
    southWest: {
      lat: 51.3066,
      lng: -10.766_600_1,
    },
  },
  [CountryCode.IT]: {
    northEast: {
      lat: 47.092,
      lng: 18.797_599_9,
    },
    southWest: {
      lat: 35.4897,
      lng: 6.626_720_1,
    },
  },
  [CountryCode.KY]: {
    northEast: {
      lat: 19.7616,
      lng: -79.7191,
    },
    southWest: {
      lat: 19.253_899_9,
      lng: -81.429_400_100_000_01,
    },
  },
  [CountryCode.LI]: {
    northEast: {
      lat: 47.270_574_300_000_01,
      lng: 9.635_692_2,
    },
    southWest: {
      lat: 47.048_428_000_000_01,
      lng: 9.471_682_999_999_999,
    },
  },
  [CountryCode.LU]: {
    northEast: {
      lat: 50.182_787,
      lng: 6.531_200_3,
    },
    southWest: {
      lat: 49.447_879,
      lng: 5.735_701_9,
    },
  },
  [CountryCode.MF]: {
    northEast: {
      lat: 18.190_277_8,
      lng: -62.764_406_3,
    },
    southWest: {
      lat: 17.896_353_5,
      lng: -63.360_564_3,
    },
  },
  [CountryCode.MG]: {
    northEast: {
      lat: -11.436_999_9,
      lng: 50.998_500_1,
    },
    southWest: {
      lat: -26.2146,
      lng: 42.7368,
    },
  },
  [CountryCode.MU]: {
    northEast: {
      lat: -19.958_717_533_457_538,
      lng: 57.879_365_107_154_8,
    },
    southWest: {
      lat: -20.550_155_589_083_104,
      lng: 57.273_860_008_611_76,
    },
  },
  [CountryCode.MX]: {
    northEast: {
      lat: 32.718_753_8,
      lng: -86.5887,
    },
    southWest: {
      lat: 14.3895,
      lng: -118.652_300_1,
    },
  },
  [CountryCode.NL]: {
    northEast: {
      lat: 53.6316,
      lng: 7.227_510_199_999_999,
    },
    southWest: {
      lat: 50.750_352_600_000_01,
      lng: 3.331_600_1,
    },
  },
  [CountryCode.PA]: {
    northEast: {
      lat: 9.714_500_1,
      lng: -77.158_487_9,
    },
    southWest: {
      lat: 7.0409,
      lng: -83.052_581_300_000_01,
    },
  },
  [CountryCode.PL]: {
    northEast: {
      lat: 54.905_476_1,
      lng: 24.145_874_3,
    },
    southWest: {
      lat: 49.002_046_000_000_01,
      lng: 14.122_864_1,
    },
  },
  [CountryCode.PT]: {
    northEast: {
      lat: 42.156,
      lng: -6.187,
    },
    southWest: {
      lat: 36.711,
      lng: -9.837,
    },
  },
  [CountryCode.SM]: {
    northEast: {
      lat: 43.993154845866144,
      lng: 12.518217650040356,
    },
    southWest: {
      lat: 43.89365049674052,
      lng: 12.402634517547426,
    },
  },
  [CountryCode.TC]: {
    northEast: {
      lat: 22.001_628_5,
      lng: -71.059_499_899_999_99,
    },
    southWest: {
      lat: 21.145_992_2,
      lng: -72.520_699_999_999_99,
    },
  },
  [CountryCode.US]: {
    northEast: {
      lat: 49.42,
      lng: -66.85,
    },
    southWest: {
      lat: 24.21,
      lng: -125.12,
    },
  },
  [CountryCode.UY]: {
    northEast: {
      lat: -30.085_214_9,
      lng: -53.077_928_4,
    },
    southWest: {
      lat: -35.155_800_1,
      lng: -58.491_360_9,
    },
  },
  [CountryCode.VG]: {
    northEast: {
      lat: 18.4239,
      lng: -64.4391,
    },
    southWest: {
      lat: 17.548_299_9,
      lng: -65.1101,
    },
  },
  [CountryCode.ZA]: {
    northEast: {
      lat: -22.125_423_9,
      lng: 38.221_690_4,
    },
    southWest: {
      lat: -47.131_348_9,
      lng: 16.281_699_9,
    },
  },
};

export const DEFAULT_IMAGE = 'https://uploadcare.engelvoelkers.com/4a6e9b16-f2a8-4afc-ba26-a7d0cbcb1dbb/';

export type GetUploadCareImageUrlParams = {
  imageId?: string;
  useWaterMark?: boolean;
  isBlurred?: boolean;
};

export function getUploadCareImageUrl({ imageId, useWaterMark, isBlurred }: GetUploadCareImageUrlParams): string {
  if (!imageId) return DEFAULT_IMAGE;
  const IMAGE_WATERMARK_ID = '131ecb57-f36f-4015-aee3-5851eb9bfa9d';
  return `https://uploadcare.engelvoelkers.com/${imageId}/${useWaterMark ? `-/overlay/${IMAGE_WATERMARK_ID}/60px60p/50p,50p/60p/` : ''}${isBlurred ? `-/blur/200/` : ''}`;
}
